<template>
  <div class="message-blocks">
    <div v-for="(block, index) in blocks" :key="index" class="block-row">
      <p v-if="block.type === 'text'" class="block block-text"><span
        v-for="(part, partIndex) in parseInline(block.value)"
        :key="partIndex"
        :class="{ bold: part.bold }"
      >{{ part.text }}</span></p>
      <div v-else-if="block.type === 'image'" class="block block-image-wrapper">
        <lottie-player
          v-if="!loadedImages[index]"
          src="/animations/chat-pre-loading.json"
          autoplay
          loop
          class="block-image-loading"
        />
        <img
          v-show="loadedImages[index]"
          class="block-image"
          :src="block.url"
          alt=""
          @click="$emit('image-open', block)"
          @load="$set(loadedImages, index, true)"
          @error="$set(loadedImages, index, true)"
        />
      </div>
      <a
        v-else-if="block.type === 'file'"
        class="block block-file"
        :href="block.url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span class="file-icon">📄</span>
        <span class="file-name">{{ block.name || FILE_FALLBACK_NAME }}</span>
      </a>
      <div v-else-if="block.type === 'video'" class="block block-video-wrapper">
        <lottie-player
          v-if="!loadedVideos[index]"
          src="/animations/chat-pre-loading.json"
          autoplay
          loop
          class="block-video-loading"
        />
        <video
          v-show="loadedVideos[index]"
          class="block-video"
          :src="block.url"
          controls
          playsinline
          preload="metadata"
          @loadeddata="$set(loadedVideos, index, true)"
          @error="$set(loadedVideos, index, true)"
        />
      </div>
      <div
        v-else-if="block.type === 'notice'"
        class="block block-notice"
        :class="block.variant ? `block-notice--${block.variant}` : ''"
      >
        {{ block.value }}
      </div>

      <div v-else-if="block.type === 'csat'" class="block csat-card">
        <template v-if="block.status === 'submitted'">
          <div class="csat-thanks">{{ CSAT_THANKS_MESSAGE }}</div>
        </template>
        <template v-else>
          <div class="csat-title">{{ block.title }}</div>
          <div class="csat-stars">
            <button
              v-for="star in 5"
              :key="star"
              type="button"
              class="csat-star"
              :class="{ 'csat-star--on': star <= (block.score || 0) }"
              :aria-label="`${star}`"
              @click="$emit('csat-score', { block, score: star })"
            >★</button>
          </div>
          <textarea
            class="csat-comment"
            :value="block.comment || ''"
            :placeholder="CSAT_COMMENT_PLACEHOLDER"
            rows="3"
            @input="$emit('csat-comment', { block, comment: $event.target.value })"
          />
          <button
            type="button"
            class="csat-submit"
            :disabled="!block.score || block.status === 'loading'"
            @click="$emit('csat-submit', block)"
          >{{ CSAT_SUBMIT_LABEL }}</button>
        </template>
      </div>

      <!-- Client-only interactive escalation blocks. These are appended by Chatbot
           (never accepted from the server — they are NOT in KNOWN_BLOCK_TYPES), so the
           server-content security model is unchanged. Rendered with native buttons that
           emit Vue events; no v-html, no URLs. -->
      <div v-else-if="block.type === 'escalation-consent'" class="block escalation-card">
        <div v-if="block.status === 'dismissed'" class="escalation-dismissed">{{ DISMISSED_TEXT }}</div>
        <template v-else>
          <div class="escalation-prompt">{{ CONSENT_PROMPT_TEXT }}</div>
          <div class="escalation-buttons">
            <button
              type="button"
              class="escalation-btn escalation-btn--primary"
              :disabled="block.status === 'loading'"
              @click="$emit('escalation-accept', block)"
            >{{ block.status === 'loading' ? LOADING_TEXT : ACCEPT_TEXT }}</button>
            <button
              type="button"
              class="escalation-btn escalation-btn--secondary"
              :disabled="block.status === 'loading'"
              @click="$emit('escalation-dismiss', block)"
            >{{ DISMISS_TEXT }}</button>
          </div>
          <div v-if="block.status === 'error'" class="escalation-error">{{ ERROR_TEXT }}</div>
        </template>
      </div>

      <div v-else-if="block.type === 'escalation-open-elsewhere'" class="block escalation-card">
        <div class="escalation-prompt">{{ OPEN_ELSEWHERE_TEXT }}</div>
        <button
          type="button"
          class="escalation-btn escalation-btn--primary escalation-btn--full"
          @click="$emit('escalation-open-elsewhere', block)"
        >{{ OPEN_ELSEWHERE_BUTTON_TEXT }}</button>
      </div>

      <div v-else-if="block.type === 'escalation-transferred'" class="block escalation-card">
        <div class="escalation-prompt">{{ TRANSFERRED_TEXT }}</div>
        <button
          type="button"
          class="escalation-btn escalation-btn--primary escalation-btn--full"
          :disabled="block.status === 'loading'"
          @click="$emit('escalation-open', block)"
        >{{ block.status === 'loading' ? LOADING_TEXT : OPEN_CONVERSATION_TEXT }}</button>
        <div v-if="block.status === 'error'" class="escalation-error">{{ ERROR_TEXT }}</div>
      </div>

      <span v-else class="block block-unknown">[unknown block type: {{ block.type }}]</span>
    </div>
  </div>
</template>

<script>
import {
  CSAT_COMMENT_PLACEHOLDER,
  CSAT_SUBMIT_LABEL,
  CSAT_THANKS_MESSAGE,
} from '@/constants/chat';
// Text-only renderer for the strict JSON chat contract (see
// docs/CHAT-JSON-CONTRACT.md). Every server value flows through Vue's `{{ }}`
// interpolation which HTML-escapes by default — no v-html, no DOM parsing,
// no URL fetches from server content. Unknown block types fail closed with a
// visible placeholder.
//
// The `escalation-consent` / `escalation-transferred` blocks are the one
// exception: they are interactive, but they are CLIENT-GENERATED only
// (appended by Chatbot when the server flags shouldEscalate) and are NOT in
// the server-validated KNOWN_BLOCK_TYPES — so the server can never inject one.
// They render static Hebrew labels + native buttons that emit Vue events; no
// server-supplied content, no URLs.
//
// image and file are rendered too, but only ever pointing at our own proxy
// path (enforced in chat-validation.js's isValidBlock, not here) — never a
// host chosen by server content. button-card and audio remain reserved.

import parseInline from './chat-formatting';

export default {
  name: 'MessageBlocks',
  props: {
    blocks: {
      type: Array,
      required: true,
    },
  },
  emits: [
    'escalation-accept', 'escalation-dismiss', 'escalation-open', 'escalation-open-elsewhere',
    'csat-score', 'csat-comment', 'csat-submit',
  ],
  data() {
    return {
      // Keyed by block index (this instance's own blocks array, not shared
      // across messages), so a slow image shows the same pre-loading
      // animation used while the chat itself is opening instead of a blank gap.
      loadedImages: {},
      loadedVideos: {},
      CSAT_COMMENT_PLACEHOLDER,
      CSAT_SUBMIT_LABEL,
      CSAT_THANKS_MESSAGE,
      CONSENT_PROMPT_TEXT: 'להעביר אותך לשיחה עם הצוות?',
      ACCEPT_TEXT: 'כן',
      DISMISS_TEXT: 'לא, תודה',
      LOADING_TEXT: 'פותח צ׳אט עם נציג',
      DISMISSED_TEXT: 'השיחה לא הועברה לטיפול הצוות.',
      ERROR_TEXT: 'משהו השתבש בפתיחת השיחה עם הנציג. אפשר לנסות שוב',
      TRANSFERRED_TEXT: 'השיחה עברה לטיפול הצוות',
      OPEN_CONVERSATION_TEXT: 'לפתיחת השיחה',
      OPEN_ELSEWHERE_TEXT: 'כבר יש לך שיחה פתוחה עם נציג',
      OPEN_ELSEWHERE_BUTTON_TEXT: 'למעבר אליה לחץ כאן',
      FILE_FALLBACK_NAME: 'קובץ',
    };
  },
  methods: {
    parseInline,
  },
};
</script>

<style scoped lang="scss">
.message-blocks {
  direction: rtl;
  text-align: right;
}

.block-row {
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.block-text {
  margin: 0;
  max-width: 100%;
  font-size: 18px;
  line-height: 1.5;
  color: #202125;
  white-space: pre-wrap;
  word-wrap: break-word;
  -webkit-font-smoothing: subpixel-antialiased;
  -moz-osx-font-smoothing: auto;

  .bold {
    font-weight: 600;
  }
}

.block-unknown {
  display: inline-block;
  font-size: 13px;
  color: #c33;
  font-style: italic;
}

.block-image-wrapper {
  display: block;
}

.block-image-loading {
  display: block;
  width: 160px;
  height: 160px;
  border-radius: 12px;
}

.block-image {
  display: block;
  max-width: 220px;
  max-height: 220px;
  width: auto;
  height: auto;
  border-radius: 12px;
  cursor: pointer;
}

.block-file {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 260px;
  padding: 12px 16px;
  background-color: #F5F5F5;
  border-radius: 12px;
  color: #202125;
  text-decoration: none;
}

.file-icon {
  flex-shrink: 0;
  font-size: 22px;
  line-height: 1;
}

.file-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 15px;
}

.block-video-wrapper {
  display: block;
}

.block-video-loading {
  display: block;
  width: 160px;
  height: 160px;
  border-radius: 12px;
}

.block-video {
  display: block;
  max-width: 260px;
  max-height: 320px;
  width: 100%;
  border-radius: 12px;
}

/* Notice block — V1's validation/rate-limit/error message styling. */
.block-notice {
  width: 95%;
  padding: 20px;
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  direction: rtl;
  color: #202125;
}

.block-notice--validation,
.block-notice--error {
  background-color: #FFCEB9;
}

.block-notice--rate-limit {
  background-color: #FFEFC3;
}

/* Figma: intercom in RiseUp Chat, node 17092:76117 — a centred strip marking a
   change of who is answering, so it is deliberately not styled as an error.
   Overrides the shared notice padding, radius and size rather than inheriting. */
.block-notice--system {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90%;
  background-color: #FFEFC3;
  border-radius: 14px;
  padding: 12px 21px 14px;
  font-size: 14px;
  line-height: 20px;
  text-decoration: underline;
}

/* Figma: intercom in RiseUp Chat — the card shown once the team closes the
   conversation. Replaces Intercom's own rating, which the messenger used to ask
   for and which cannot be submitted through their API. */
.csat-card {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #ffffff;
  border-radius: 20px;
  padding: 24px 20px;
  margin: 6px 0;
}

.csat-title {
  font-size: 20px;
  line-height: 26px;
  color: #202125;
  text-align: center;
  margin-bottom: 20px;
}

.csat-stars {
  display: flex;
  gap: 19px;
  margin-bottom: 20px;
}

.csat-star {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  font-size: 37px;
  line-height: 1;
  color: #d9d9d9;
}

.csat-star--on {
  color: #ffc531;
}

.csat-comment {
  width: 297px;
  max-width: 100%;
  height: 116px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  border-radius: 14px;
  padding: 12px 16px;
  font-family: inherit;
  font-size: 16px;
  direction: rtl;
  text-align: right;
  resize: none;
  margin-bottom: 20px;
}

.csat-submit {
  width: 303px;
  max-width: 100%;
  height: 50px;
  border: none;
  border-radius: 13px;
  background-color: #5d7afd;
  color: #ffffff;
  font-family: inherit;
  font-size: 18px;
  cursor: pointer;
}

.csat-submit:disabled {
  opacity: 0.4;
  cursor: default;
}

.csat-thanks {
  font-size: 18px;
  line-height: 26px;
  color: #202125;
  text-align: center;
}

/* Escalation consent / transferred cards — mirror V1's escalation styling. */
.escalation-card {
  width: 100%;
  box-sizing: border-box;
  background-color: #DCE8F6;
  border-radius: 8px;
  padding: 24px 20px;
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.escalation-prompt {
  font-size: 16px;
  line-height: 24px;
  color: #202125;
  text-align: center;
}

.escalation-buttons {
  display: flex;
  direction: rtl;
  gap: 12px;
  justify-content: center;
}

.escalation-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 24px;
  border-radius: 16px;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
}

.escalation-btn--primary {
  min-width: 89px;
  background-color: #5D7AFD;
  color: #FFFFFF;
  border: none;
}

.escalation-btn--secondary {
  min-width: 94px;
  background-color: transparent;
  color: #5D7AFD;
  border: 1px solid #5D7AFD;
}

.escalation-btn--full {
  width: 100%;
}

.escalation-dismissed {
  font-size: 16px;
  color: #202125;
  text-align: center;
}

.escalation-error {
  font-size: 14px;
  color: #C24A2C;
  text-align: center;
}
</style>
